import React, { JSX, useEffect, useRef, useState } from 'react'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { format, parse, isValid } from 'date-fns'
import { calendarIcon } from './Icons'
import { QuickRangePreset, QUICK_RANGE_LABELS, startOfDay, endOfDay, getQuickRange, getQuickRangePreset } from 'lib/timeRanges'

const today = new Date()

const QUICK_RANGES: QuickRangePreset[] = ['24h', '30d', '365d']

const formatDate = (date: Date): string => format(date, 'yyyy-MM-dd')

const getQuickRangeLabel = (start: Date, end: Date): string | null => {
  const preset = getQuickRangePreset(start, end)
  return preset != null ? QUICK_RANGE_LABELS[preset] : null
}

const parseDateInput = (value: string): Date | null => {
  const parsed = parse(value, 'yyyy-MM-dd', new Date())
  return isValid(parsed) ? parsed : null
}
export const TimeRangeDropdown = ({
  start,
  end,
  onRangeSelect,
  disabled
}: {
  start: Date
  end: Date
  onRangeSelect: (start: Date, end: Date, preset?: QuickRangePreset) => void
  disabled: boolean
}): JSX.Element => {
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)
  const [startDate, setStartDate] = useState(start)
  const [endDate, setEndDate] = useState(end)
  const [startInput, setStartInput] = useState(formatDate(start))
  const [endInput, setEndInput] = useState(formatDate(end))
  const [error, setError] = useState<string | null>(null)
  const [quickLabel, setQuickLabel] = useState<string | null>(null)
  const [currentMonth, setCurrentMonth] = useState(endDate)
  const [lastConfirmedStart, setLastConfirmedStart] = useState(start)
  const [lastConfirmedEnd, setLastConfirmedEnd] = useState(end)

  const toggleDropdown = (): void => setOpen(prev => !prev)

  const handleQuickSelect = (preset: QuickRangePreset): void => {
    const { startDate: newStart, endDate: newEnd } = getQuickRange(preset)

    onRangeSelect(newStart, newEnd, preset)
    setLastConfirmedStart(newStart)
    setLastConfirmedEnd(newEnd)
    setOpen(false)
    setError(null)
  }

  const handleInputChange = (value: string, type: 'start' | 'end'): void => {
    if (type === 'start') setStartInput(value)
    else setEndInput(value)

    const parsedStart = parseDateInput(type === 'start' ? value : startInput)
    const parsedEnd = parseDateInput(type === 'end' ? value : endInput)

    if ((parsedStart == null) || (parsedEnd == null) || parsedStart > parsedEnd) {
      setError('Invalid date range.')
      return
    }
    setStartDate(parsedStart)
    setEndDate(parsedEnd)
  }

  const handleCancel = (): void => {
    setOpen(false)
  }

  const handleApply = (): void => {
    const parsedStart = parseDateInput(startInput)
    const parsedEnd = parseDateInput(endInput)
    if ((parsedStart == null) || (parsedEnd == null) || parsedStart > parsedEnd) {
      setError('Invalid date range.')
      return
    }

    const normalizedStart = startOfDay(parsedStart)
    const normalizedEnd = endOfDay(parsedEnd)

    setLastConfirmedStart(normalizedStart)
    setLastConfirmedEnd(normalizedEnd)

    setQuickLabel(getQuickRangeLabel(normalizedStart, normalizedEnd))
    onRangeSelect(normalizedStart, normalizedEnd, getQuickRangePreset(normalizedStart, normalizedEnd))
    setOpen(false)
  }

  const handleCalendarSelect = (range: { from?: Date, to?: Date } | undefined): void => {
    if (((range?.from) == null) || ((range?.to) == null) || range.from > range.to) return

    setStartDate(startOfDay(range.from))
    setEndDate(endOfDay(range.to))
    setStartInput(formatDate(range.from))
    setEndInput(formatDate(range.to))
    setError(null)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if ((dropdownRef.current != null) && !dropdownRef.current.contains(e.target as Node)) {
        handleCancel()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    setStartInput(formatDate(start))
    setEndInput(formatDate(end))
    setQuickLabel(getQuickRangeLabel(start, end))
  }, [start, end])

  useEffect(() => {
    if (open) {
      setStartInput(formatDate(lastConfirmedStart))
      setEndInput(formatDate(lastConfirmedEnd))
      setStartDate(lastConfirmedStart)
      setEndDate(lastConfirmedEnd)
      setCurrentMonth(lastConfirmedEnd)
      setError(null)
    }
  }, [open])

  const displayLabel = quickLabel ?? `${format(startDate, 'M/d/yyyy')} – ${format(endDate, 'M/d/yyyy')}`
  const errorBlock = <div data-testid='error-block-time' className='mt-2 text-center text-sm bg-error text-white py-1 rounded'>{error}</div>
  const errorExists = error !== null
  const errorMessage = errorExists ? errorBlock : null
  const errorClass = errorExists ? 'border-red-500' : ''
  const disabledClass = disabled ? 'pointer-events-none opacity-50' : ''

  return (
    <>
      <button
        data-testid='time-range-toggle'
        disabled={disabled}
        onClick={toggleDropdown}
        className={`relative w-full rounded-md px-3 py-2 text-sm font-semibold shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 ring-1 ring-inset text-left truncate overflow-hidden whitespace-nowrap 
      ${disabledClass}
    `}
      >
        <div className='flex gap-2' data-testid='time-range-placeholder'>
          {calendarIcon}
          {displayLabel}
        </div>
      </button>
      {open && (
        <div
          ref={dropdownRef}
          className='absolute z-50 mt-2 w-85 origin-top-right rounded-md bg-lm-bg-main dark:bg-dm-bg-main ring-1 shadow-lg dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 p-4'
        >
          <DayPicker
            data-testid='day-picker'
            mode='range'
            selected={{ from: startDate, to: endDate }}
            onSelect={handleCalendarSelect}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            showOutsideDays
            disabled={{ after: today }}
            modifiersClassNames={{
              nav: 'flex justify-between',
              caption: 'text-lg font-semibold',
              selected: 'text-lm-text-primary dark:text-dm-text-primary',
              range_middle: 'bg-lm-bg-secondary dark:bg-dm-bg-secondary'
            }}
          />

          <div className='flex justify-between mt-2 text-sm text-lm-text-primary dark:text-dm-text-primary gap-4'>
            <span>
              <strong>Start:</strong>{' '}
              <input
                data-testid='start-date-input'
                type='text'
                value={startInput}
                onChange={(e) => handleInputChange(e.target.value, 'start')}
                placeholder='Start date'
                className={`border rounded px-2 py-1 text-sm w-36 ${errorClass}`}
              />
            </span>
            <span>
              <strong>End:</strong>{' '}
              <input
                data-testid='end-date-input'
                type='text'
                value={endInput}
                onChange={(e) => handleInputChange(e.target.value, 'end')}
                placeholder='End date'
                className={`border rounded px-2 py-1 text-sm w-36 ${errorClass}`}
              />
            </span>
          </div>
          {errorMessage}
          <div className='mt-3 flex gap-2'>
            {QUICK_RANGES.map((preset) => (
              <button
                key={preset}
                data-testid={`quick-${preset}`}
                onClick={() => handleQuickSelect(preset)}
                className='flex-1 rounded-md bg-lm-bg-secondary dark:bg-dm-bg-secondary hover:bg-lm-bg-accent dark:hover:bg-dm-bg-accent text-sm py-1.5'
              >
                {preset}
              </button>
            ))}
          </div>
          <div className='flex justify-between mt-2 text-sm text-lm-text-secondary dark:text-dm-text-secondary gap-4'>
            <button data-testid='cancel-button-time' key='cancel' onClick={handleCancel} className='flex-1 rounded-md outline-lm-text-accent dark:outline-dm-text-accent outline-1 hover:bg-lm-bg-secondary dark:bg-dm-bg-secondary text-sm py-1.5'>Cancel </button>
            <button data-testid='apply-button-time' key='apply' onClick={handleApply} className='flex-1 rounded-md text-white bg-brand hover:bg-brand/70 text-sm py-1.5'>Apply </button>
          </div>
        </div>
      )}
    </>
  )
}
